import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Row, Col, Card, CardBody, CardHeader } from 'reactstrap'
import McDataServices from '../../services/McDataServices'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { RSelect } from '../../components/Component'

const AssignPackageModal = ({ isOpen, toggle, selectedUser }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [options, setOptions] = useState([])
  const [selectedPackage, setSelectedPackage] = useState(null)

  const [transferRemainingLimit, setTransferRemainingLimit] = useState(false)
  const [remainingLimits, setRemainingLimits] = useState(null)
  const [fetchingLimits, setFetchingLimits] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setSelectedPackage(null)
      setTransferRemainingLimit(false)
      setRemainingLimits(null)
      fetchPackages()
      if (selectedUser?.id) {
        fetchRemainingLimits(selectedUser.id)
      }
    }
  }, [isOpen])

  const fetchPackages = async () => {
    setLoading(true)
    try {
      const response = await McDataServices.getPackages()
      const data = response.data || response
      const packagesList = data.content || data.packages || (Array.isArray(data) ? data : [])

      const newOptions = packagesList.map(pkg => ({
        value: pkg.name,
        label: pkg.name
      }))
      setOptions(newOptions)
    } catch (error) {
      console.error('Error fetching packages:', error)
      toast.error(t('admin.modal.fetchError', 'Failed to fetch packages.'))
    } finally {
      setLoading(false)
    }
  }

  const fetchRemainingLimits = async (userId) => {
    setFetchingLimits(true)
    try {
      const data = await McDataServices.getRemainingLimits(userId)
      setRemainingLimits(data)
      if (data?.packageName === 'Demo Package') {
        setTransferRemainingLimit(false)
      }
    } catch (error) {
      console.error('Failed to fetch remaining limits', error)
      setRemainingLimits(null)
    } finally {
      setFetchingLimits(false)
    }
  }

  const handleSave = async () => {
    if (!selectedPackage || !selectedUser) return
    setSaving(true)

    try {
      const payload = {
        userName: selectedUser.userName,
        packageName: selectedPackage.value,
        transferRemainingLimit: remainingLimits?.packageName === 'Demo Package' ? false : transferRemainingLimit
      }

      await McDataServices.assignPackage(payload)
      toast.success(t('admin.modal.assignSuccess', 'Package assigned successfully.'), {
        position: 'top-right',
        autoClose: 3000
      })
      toggle()
    } catch (error) {
      console.error('Assign package failed', error)
      toast.error(error?.response?.data?.detail || t('admin.modal.assignError', 'Failed to assign package.'), {
        position: 'top-right',
        autoClose: 3000
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <style>
        {`
          .super-wide-modal .modal-content {
            background-color: #f3f4f6 !important;
            border: none !important;
            border-radius: 8px !important;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          }
          .super-wide-modal {
            max-width: 95vw !important;
            width: 95vw !important;
          }
          .limit-pane {
            border-left: 1px solid #e5e7eb;
            padding-left: 1.5rem;
          }
          @media (max-width: 991.98px) {
            .limit-pane {
              border-left: none;
              border-top: 1px solid #e5e7eb;
              padding-left: 0;
              padding-top: 1.5rem;
              margin-top: 1.5rem;
            }
          }
          .modern-card {
            background-color: #ffffff !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 6px !important;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05) !important;
          }
          .modern-card-header {
            background-color: #f9fafb !important;
            border-bottom: 1px solid #e5e7eb !important;
            padding: 0.5rem 0.75rem !important;
          }
          .modern-card-body {
            padding: 0 !important;
          }
          .limit-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.45rem 0.75rem;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.75rem;
            line-height: 1.2;
            transition: background-color 0.15s ease;
          }
          .limit-row:hover {
            background-color: #f9fafb;
          }
          .limit-row:last-child {
            border-bottom: none;
          }
          .limit-label {
            color: #4b5563;
            font-weight: 500;
            max-width: 80%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .limit-value {
            font-weight: 700;
            color: #1f2937;
            padding-left: 0.5rem;
            flex-shrink: 0;
          }
          .badge-unlimited {
            background-color: #d1fae5 !important;
            color: #065f46 !important;
            font-size: 0.7rem !important;
            padding: 0.15rem 0.35rem !important;
            border-radius: 4px !important;
            font-weight: 600 !important;
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        className={`modal-dialog-centered ${transferRemainingLimit ? 'super-wide-modal' : ''}`}
        size={transferRemainingLimit ? 'xl' : 'md'}
        close={<button className="close" onClick={toggle}>×</button>}
      >
        <ModalHeader toggle={toggle} close={<button className="close" onClick={toggle}>×</button>}>
          {t('admin.table.assignPackage', 'Assign Package')}
        </ModalHeader>
        <ModalBody>
          <Row>
            {transferRemainingLimit && (
              <Col lg="8" md="7">
                <h5 className="mb-3 font-weight-bold text-dark" style={{ fontSize: '1rem' }}>
                  {t('admin.modal.remainingLimits', 'Kalan Limitler')}
                </h5>
                {fetchingLimits && (
                  <div className="d-flex justify-content-center align-items-center py-5 h-100">
                    <Spinner color="primary"/>
                  </div>
                )}
                {!fetchingLimits && remainingLimits && (
                  <Row className="gy-3">
                    <Col md="6">
                      <Card className="modern-card h-100">
                        <CardHeader className="modern-card-header">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {t('admin.modal.generalSubscription', 'Genel Abonelik')}
                              </h6>
                              {remainingLimits.packageName && (
                                <small className="text-muted font-weight-medium">
                                  {remainingLimits.packageName}
                                </small>
                              )}
                            </div>
                            {remainingLimits.startDate && remainingLimits.expiryDate && (
                              <small className="text-muted font-weight-medium text-end" style={{ fontSize: '0.7rem' }}>
                                {remainingLimits.startDate} / {remainingLimits.expiryDate}
                              </small>
                            )}
                          </div>
                        </CardHeader>
                        <CardBody className="modern-card-body">
                          <div className="d-flex flex-column">
                            {remainingLimits.ruleMap && Object.keys(remainingLimits.ruleMap).length > 0 && (
                              Object.entries(remainingLimits.ruleMap).map(([key, val]) => (
                                <div key={key} className="limit-row">
                                  <span className="limit-label" title={t(`payment.rules.${key}`, key)}>
                                    {t(`payment.rules.${key}`, key)}
                                  </span>
                                  <div className="limit-value">
                                    {val === -1 ? <span className="badge-unlimited">{t('admin.modal.unlimited', 'Sınırsız')}</span> : val}
                                  </div>
                                </div>
                              ))
                            )}
                            {(!remainingLimits.ruleMap || Object.keys(remainingLimits.ruleMap).length === 0) && (
                              <p className="text-muted small m-3">{t('admin.modal.noRemainingLimits', 'Eski pakete ait kalan limit bulunamadı.')}</p>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col md="6">
                      <Card className="modern-card h-100">
                        <CardHeader className="modern-card-header">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-0 fw-bold text-warning" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {t('admin.modal.mailingSubscription', 'Mailing Aboneliği')}
                              </h6>
                              {remainingLimits.mailingUserSubscription?.packageName && (
                                <small className="text-muted font-weight-medium">
                                  {remainingLimits.mailingUserSubscription.packageName}
                                </small>
                              )}
                            </div>
                            {remainingLimits.mailingUserSubscription?.startDate && remainingLimits.mailingUserSubscription?.expiryDate && (
                              <small className="text-muted font-weight-medium text-end" style={{ fontSize: '0.7rem' }}>
                                {remainingLimits.mailingUserSubscription.startDate} / {remainingLimits.mailingUserSubscription.expiryDate}
                              </small>
                            )}
                          </div>
                        </CardHeader>
                        <CardBody className="modern-card-body">
                          <div className="d-flex flex-column">
                            {remainingLimits.mailingUserSubscription?.ruleMap && Object.keys(remainingLimits.mailingUserSubscription.ruleMap).length > 0 && (
                              Object.entries(remainingLimits.mailingUserSubscription.ruleMap).map(([key, val]) => (
                                <div key={key} className="limit-row">
                                  <span className="limit-label" title={t(`payment.rules.${key}`, key)}>
                                    {t(`payment.rules.${key}`, key)}
                                  </span>
                                  <div className="limit-value">
                                    {val === -1 ? <span className="badge-unlimited">{t('admin.modal.unlimited', 'Sınırsız')}</span> : val}
                                  </div>
                                </div>
                              ))
                            )}
                            {(!remainingLimits.mailingUserSubscription?.ruleMap || Object.keys(remainingLimits.mailingUserSubscription.ruleMap).length === 0) && (
                              <p className="text-muted small m-3">{t('admin.modal.noRemainingLimits', 'Eski pakete ait kalan limit bulunamadı.')}</p>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}
                {!fetchingLimits && !remainingLimits && (
                  <Card className="modern-card d-flex align-items-center justify-content-center py-5 px-3 h-100">
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                      {t('admin.modal.noRemainingLimits', 'Eski pakete ait kalan limit bulunamadı.')}
                    </p>
                  </Card>
                )}
              </Col>
            )}

            <Col lg={transferRemainingLimit ? '4' : '12'} md={transferRemainingLimit ? '5' : '12'} className={transferRemainingLimit ? 'limit-pane' : ''}>
              <p>
                {t('admin.table.assignPackageDesc', 'Select a package to assign to:')}{' '}
                <strong>{selectedUser?.email || selectedUser?.userName}</strong>
              </p>
              <FormGroup className="mb-3">
                <Label>{t('admin.modal.packageName', 'Package Name')}</Label>
                <RSelect
                  options={options}
                  value={selectedPackage}
                  onChange={(selected) => setSelectedPackage(selected)}
                  placeholder={loading ? t('admin.loading', 'Loading...') : t('sp_select', 'Select...')}
                  isDisabled={loading}
                />
              </FormGroup>

              <div className="custom-control custom-switch mb-3">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="transferRemainingLimit"
                  checked={transferRemainingLimit}
                  disabled={remainingLimits?.packageName === 'Demo Package'}
                  onChange={(e) => setTransferRemainingLimit(e.target.checked)}
                />
                <label className="custom-control-label font-weight-bold" htmlFor="transferRemainingLimit" style={{ cursor: remainingLimits?.packageName === 'Demo Package' ? 'not-allowed' : 'pointer' }}>
                  {t('admin.modal.transferRemainingLimit', 'Kalan limitlerini de taşı')}
                </label>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle}>
            {t('admin.modal.cancel', 'Cancel')}
          </Button>
          <Button color="primary" onClick={handleSave} disabled={!selectedPackage || saving}>
            {saving ? <Spinner size="sm"/> : t('admin.modal.saveChanges', 'Save Changes')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default AssignPackageModal
